import React, { useEffect, useState } from "react";
import { uploadToS3 } from "../../helper/s3";
import {
  Button, Card, Space, Table, Tag, Typography, Modal,
  Descriptions, Tooltip, Divider, Form, Input, Select,
  Upload, InputNumber, Switch, TimePicker, Grid, message
} from "antd";
import { EyeOutlined, EditOutlined, PlusOutlined, UploadOutlined, CloseOutlined, SettingOutlined, FilterOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { Severty, ShowToast } from "../../helper/toast";
import dayjs from "dayjs";
import HotelDetails from "./HotelDetails";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";



const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;
const COUNTRY_CODE_OPTIONS = ["+91", "+1", "+44", "+61", "+971", "+65"];

const HotelList = () => {
  const screens = useBreakpoint();
  const { request } = useRequest();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const filterAirlineId = searchParams.get("airline_id");

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [facilityInput, setFacilityInput] = useState("");
  const [hotelImages, setHotelImages] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 0 });
  const [addForm] = Form.useForm();
  const authRole = localStorage.getItem("authRole") || "admin";
  const [airlineOptions, setAirlineOptions] = useState([]);
  let airlineId = "";
  try {
    const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
    airlineId = profile?._id || profile?.id || "";
  } catch (e) { }


  const normalizeToArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload?.data && Array.isArray(payload.data)) return payload.data;
    if (payload?.Hotels_details && Array.isArray(payload.Hotels_details)) return payload.Hotels_details;
    if (payload?.Details && Array.isArray(payload.Details)) return payload.Details;
    return [];
  };

  const fetchHotels = (page = 1, limit = 10, search = "") => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search.trim()) params.append("search", search.trim());
    if (viewParam) params.append("view", viewParam);
    if (filterAirlineId) params.append("airline_id", filterAirlineId);

    request({
      url: `${apiPath.baseURL}/${apiPath.hotels}?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        setData(normalizeToArray(res));
        if (res?.pagination) {
          setPagination({
            total: res.pagination.total ?? 0,
            page: res.pagination.page ?? page,
            limit: res.pagination.limit ?? limit,
            totalPages: res.pagination.totalPages ?? 0,
          });
        } else {
          setPagination((prev) => ({ ...prev, page, limit }));
        }
        setLoading(false);
      },
      onError: () => {
        setData([]);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    if (authRole !== "hotel") {
      fetchHotels(1, pagination.limit, searchText);
    }
  }, [viewParam, filterAirlineId]);

  useEffect(() => {
    request({
      url: "/airline/users",
      method: "GET",
      onSuccess: (res) => {
        if (res?.success && Array.isArray(res.users)) {
          const activeUsers = res.users.filter((u) => u.status === 1);
          setAirlineOptions(activeUsers.map((u) => ({ value: u._id || u.id, label: u.name })));
        }
      },
      onError: () => {
        setAirlineOptions([]);
      },
    });
  }, []);

  if (authRole === "hotel") {
    return <HotelDetails />;
  }

  const handleAddFacility = () => {
    if (facilityInput.trim()) {
      setFacilities([...facilities, facilityInput.trim()]);
      setFacilityInput("");
    }
  };

  const handleRemoveFacility = (index) => {
    setFacilities(facilities.filter((_, i) => i !== index));
  };

  const uploadImagesToS3 = async (fileList) => {
    const urls = [];
    for (const file of fileList) {
      const url = await uploadToS3(file.originFileObj);
      if (!url) throw new Error(`Image upload failed for ${file.name}`);
      urls.push(url);
    }
    return urls;
  };

  const handleAddHotel = async (values) => {
    setAddLoading(true);
    try {
      // 1. Upload images to S3 and collect URLs
      let imageUrls = [];
      if (hotelImages.length > 0) {
        imageUrls = await uploadImagesToS3(hotelImages);
      }

      const airline_ids = Array.isArray(values.airline_ids) ? values.airline_ids : (values.airline_ids ? [values.airline_ids] : []);
      const primary_airline_id = airline_ids[0] || null;

      // 2. Build JSON payload with S3 image URLs
      const payload = {
        Hotel_name: values.hotelName,
        address: values.address,
        city: values.city,
        hotelType: values.hotelType,
        airline_id: primary_airline_id,
        airline_ids: airline_ids,
        aboutHotel: values.aboutHotel,
        commision_rate: values.commissionRate || 0,
        hotel_email: values.hotelEmail,
        hotel_country_code: values.countryCode || "+91",
        hotel_phone: String(values.contactNo || "").replace(/\D/g, ""),
        hotel_password: values.password,
        check_in_time: values.check_in_time ? dayjs(values.check_in_time).format("hh:mm A") : "",
        check_out_time: values.check_out_time ? dayjs(values.check_out_time).format("hh:mm A") : "",
        cancellation_policy: values.cancellation_policy || "",
        pet_allowed: !!values.pet_allowed,
        child_policy: values.child_policy || "",
        facilities: facilities,
        HotelImages: imageUrls,
      };

      request({
        url: `${apiPath.baseURL}${apiPath.createHotel}`,
        method: "POST",
        data: payload,
        onSuccess: (res) => {
          setAddLoading(false);
          if (res?.success) {
            ShowToast(res.message || "Hotel added successfully", Severty.SUCCESS);
            setAddModalVisible(false);
            addForm.resetFields();
            setFacilities([]);
            setHotelImages([]);
            fetchHotels(1, pagination.limit, searchText);
          } else {
            ShowToast(res.message || "Failed to add hotel", Severty.ERROR);
          }
        },
        onError: (error) => {
          setAddLoading(false);
          ShowToast(error?.response?.data?.message || "Failed to add hotel", Severty.ERROR);
        },
      });
    } catch (err) {
      setAddLoading(false);
      ShowToast(err?.message || "Image upload failed", Severty.ERROR);
    }
  };

  const getStatusInfo = (status) => {
    const isActive = status === 1 || status === "active" || status === true;
    return {
      text: isActive ? "Active" : "Inactive",
      color: isActive ? "green" : "red",
      active: isActive,
    };
  };

  const toggleHotelStatus = (record) => {
    const hotelId = record.Hotel_Id || record._id;
    if (!hotelId) {
      ShowToast("Invalid hotel ID", Severty.ERROR);
      return;
    }

    request({
      url: `${apiPath.baseURL}/active_inactive_Hotel/${hotelId}`,
      method: "POST",
      onSuccess: (res) => {
        if (res?.success) {
          const newStatus = record.status === 1 || record.status === "active" ? 0 : 1;
          setData((prev) =>
            prev.map((hotel) =>
              (hotel.Hotel_Id || hotel._id) === (record.Hotel_Id || record._id)
                ? { ...hotel, status: newStatus }
                : hotel
            )
          );
          ShowToast(res.message || "Status updated", Severty.SUCCESS);
        }
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || "Status update failed", Severty.ERROR);
      },
    });
  };

  const columns = [
    {
      title: "Hotel ID",
      dataIndex: "Hotel_Id",
      key: "hotelId",
      width: 120,
      render: (_, record) => record.Hotel_Id || record._id?.slice(-6) || "-",
    },
    {
      title: "Hotel Name",
      dataIndex: "Hotel_name",
      key: "hotelName",
      render: (_, record) => record.Hotel_name || record.name || "-",
    },
    {
      title: "Type",
      dataIndex: "hotelType",
      key: "hotelType",
      render: (_, record) => record.hotelType || "-",
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (_, record) => record.city || record.location?.city || "-",
    },
    {
      title: "Total Rooms",
      key: "totalRooms",
      width: 120,
      render: (_, record) => record.room_stats?.total ?? 0,
      onCell: () => ({
        style: {
          background: viewParam === "total_rooms" ? "#e6f7ff" : undefined,
          fontWeight: viewParam === "total_rooms" ? 600 : undefined
        }
      })
    },
    {
      title: "Rooms Used",
      key: "usedRooms",
      width: 120,
      render: (_, record) => record.room_stats?.used ?? 0,
      onCell: () => ({
        style: {
          background: (viewParam === "used_rooms" || viewParam === "usedRooms") ? "#fff0f6" : undefined,
          fontWeight: (viewParam === "used_rooms" || viewParam === "usedRooms") ? 600 : undefined
        }
      })
    },
    {
      title: "Rooms Remaining",
      key: "remainingRooms",
      width: 150,
      render: (_, record) => record.room_stats?.remaining ?? 0,
      onCell: () => ({
        style: {
          background: (viewParam === "remaining_rooms" || viewParam === "remainingRooms") ? "#f9f0ff" : undefined,
          fontWeight: (viewParam === "remaining_rooms" || viewParam === "remainingRooms") ? 600 : undefined
        }
      })
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const { text, color } = getStatusInfo(status);
        return (
          <Tag
            color={color}
            style={{ fontWeight: 600, cursor: "pointer", userSelect: "none" }}
            onClick={() => toggleHotelStatus(record)}
          >
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => (
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{new Date(createdAt).toLocaleString()}</span>
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Hotel Details">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedHotel(record);
                setDetailModalVisible(true);
              }}
            />
          </Tooltip>
          {authRole === "airline" ? (
            <Tooltip title="Room Availability">
              <Button
                type="primary"
                size="small"
                icon={<SettingOutlined />}
                onClick={() => navigate(`/hotel-management/${record.Hotel_Id || record._id}`)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Edit Hotel">
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/hotel-management/${record.Hotel_Id || record._id}`)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];


  return (
    <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 8px 24px rgba(19, 34, 114, 0.08)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <Title level={3} style={{ margin: 0, fontWeight: 700 }}>Hotel Management</Title>
        <Space wrap>
          <Input
            placeholder="Search hotels..."
            value={searchText}
            onChange={(e) => {
              const val = e.target.value;
              setSearchText(val);
              fetchHotels(1, pagination.limit, val);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            style={{ width: screens.xs ? 190 : 250 }}
            allowClear
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setAddModalVisible(true);
            }}
          >
            Add New Hotel
          </Button>
        </Space>
      </div>

      {viewParam && (
        <Card 
          style={{ 
            marginBottom: 16, 
            borderRadius: 10, 
            background: "#f0f5ff", 
            border: "1px solid #adc6ff" 
          }}
          bodyStyle={{ padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Space>
            <FilterOutlined style={{ color: "#2f54eb", fontSize: 16 }} />
            <Text>
              Active Filter: Showing hotels with{" "}
              <strong>
                {viewParam === "total_rooms" ? "Total Rooms" : ""}
                {(viewParam === "used_rooms" || viewParam === "usedRooms") ? "Used Rooms" : ""}
                {(viewParam === "remaining_rooms" || viewParam === "remainingRooms") ? "Remaining Rooms" : ""}
              </strong>
              {filterAirlineId && airlineOptions.length > 0 && ` for airline: ${airlineOptions.find(a => a.value === filterAirlineId)?.label || filterAirlineId}`}
            </Text>
          </Space>
          <Button 
            type="link" 
            onClick={() => {
              navigate("/hotel-management");
            }}
            style={{ padding: 0 }}
          >
            Clear Filter
          </Button>
        </Card>
      )}

      <Table
        rowKey={(record) => record.Hotel_Id || record._id}
        loading={loading}
        dataSource={data}
        scroll={{ x: 1000 }}
        columns={columns}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          showTotal: (total, range) =>
            `Showing ${range[0]}–${range[1]} of ${total} hotel${total !== 1 ? "s" : ""}`,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({ ...prev, page, limit: pageSize }));
            fetchHotels(page, pageSize, searchText);
          },
        }}
      />


      {/* View Details Modal */}
      <Modal
        width={screens.xs ? "95%" : 900}
        title={<div style={{ textAlign: "center", fontSize: 20, fontWeight: 600 }}>{selectedHotel?.Hotel_name || "Hotel Details"}</div>}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        centered
        bodyStyle={{ padding: screens.xs ? 12 : 24, maxHeight: screens.xs ? "75vh" : "80vh", overflowY: "auto" }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>Close</Button>
        ]}
      >
        {selectedHotel && (
          <>
            <div style={{ marginBottom: 24, display: "flex", justifyContent: "center", gap: 12 }}>
              <Tag color={getStatusInfo(selectedHotel.status).color}>{getStatusInfo(selectedHotel.status).text}</Tag>
              <Tag>{selectedHotel.hotelType || "Standard"}</Tag>
            </div>

            <Descriptions
              bordered
              column={screens.xs ? 1 : 2}
              size={screens.xs ? "small" : "default"}
              labelStyle={{ fontWeight: 600, backgroundColor: "#fafafa", width: screens.xs ? 120 : "auto" }}
            >
              <Descriptions.Item label="Hotel ID">
                <span style={{ wordBreak: "break-all" }}>
                  {selectedHotel.Hotel_Id || selectedHotel._id?.slice(-6)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Hotel Email">
                <span style={{ wordBreak: "break-all" }}>{selectedHotel.hotel_email || "-"}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Contact No">
                <span style={{ wordBreak: "break-all" }}>
                  {selectedHotel.hotel_country_code || "+91"} {selectedHotel.hotel_phone || selectedHotel.contact_no || "-"}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Address">
                <span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>{selectedHotel.address || "-"}</span>
              </Descriptions.Item>
              <Descriptions.Item label="City">{selectedHotel.city}</Descriptions.Item>
              {/* <Descriptions.Item label="Commission Rate">
                <Text strong style={{ color: "#52c41a" }}>{selectedHotel.commision_rate}%</Text>
              </Descriptions.Item> */}
              <Descriptions.Item label="Pets Allowed">
                <Tag color={selectedHotel.pet_allowed ? "green" : "red"}>{selectedHotel.pet_allowed ? "Yes" : "No"}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Check-in Time">{selectedHotel.check_in_time || "-"}</Descriptions.Item>
              <Descriptions.Item label="Check-out Time">{selectedHotel.check_out_time || "-"}</Descriptions.Item>
              <Descriptions.Item label="Airport Selection">
                {selectedHotel ? (
                  selectedHotel.airline_ids && selectedHotel.airline_ids.length > 0 ? (
                    selectedHotel.airline_ids.map(id => {
                      return airlineOptions.find(o => o.value === id)?.label || id;
                    }).join(", ")
                  ) : (
                    airlineOptions.find(o => o.value === selectedHotel.airline_id)?.label || selectedHotel.airline_id || "-"
                  )
                ) : "-"}
              </Descriptions.Item>
            </Descriptions>

            <Divider orientation="left">Policies</Divider>
            <div
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: screens.xs ? "110px 1fr" : "160px 1fr",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    background: "#fafafa",
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.65)",
                    padding: "10px 12px",
                    borderRight: "1px solid #f0f0f0",
                  }}
                >
                  Child Policy
                </div>
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 220, overflowY: "auto", paddingRight: 8 }}>
                    {selectedHotel.child_policy || "N/A"}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: screens.xs ? "110px 1fr" : "160px 1fr",
                }}
              >
                <div
                  style={{
                    background: "#fafafa",
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.65)",
                    padding: "10px 12px",
                    borderRight: "1px solid #f0f0f0",
                  }}
                >
                  Cancellation Policy
                </div>
                <div style={{ padding: "10px 12px" }}>
                  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 160, overflowY: "auto", paddingRight: 8 }}>
                    {selectedHotel.cancellation_policy || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            {selectedHotel.aboutHotel && (
              <>
                <Divider orientation="left">About Hotel</Divider>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 240,
                    overflowY: "auto",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                    padding: "10px 12px",
                    background: "#fff",
                  }}
                >
                  <Paragraph style={{ marginBottom: 0 }}>{selectedHotel.aboutHotel}</Paragraph>
                </div>
              </>
            )}

            {/* <Divider orientation="left">Facilities & Amenities</Divider> */}
            {/* <div style={{ maxHeight: 160, overflowY: "auto", paddingRight: 6 }}>
              <Space wrap>
                {selectedHotel.facilities?.length
                  ? selectedHotel.facilities.map((f, i) => <Tag key={i} color="blue">{f}</Tag>)
                  : "None"}
              </Space>
            </div> */}
          </>
        )}
      </Modal>

      {/* Add Hotel Modal */}
      <Modal
        width={750}
        title={<div style={{ fontSize: 24, fontWeight: 700, textAlign: "center" }}>Add New Hotel</div>}
        open={addModalVisible}
        wrapClassName="add-hotel-modal"
        onCancel={() => {
          setAddModalVisible(false);
          addForm.resetFields();
          setFacilities([]);
          setHotelImages([]);
        }}
        footer={null}
      >
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddHotel}
          initialValues={{
            pet_allowed: false,
            countryCode: "+91",
            airline_ids: authRole === "airline" ? [airlineId] : undefined,
            check_in_time: dayjs().hour(12).minute(0).second(0),
            check_out_time: dayjs().hour(11).minute(0).second(0)
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item label="Hotel Name" name="hotelName" rules={[{ required: true, whitespace: true, message: "Hotel name is required" }]}><Input placeholder="Enter hotel name" /></Form.Item>
            <Form.Item label="City" name="city" rules={[{ required: true, whitespace: true, message: "City is required" }]}><Input placeholder="Enter city" /></Form.Item>
            <Form.Item label="Hotel Type" name="hotelType" rules={[{ required: true, message: "Hotel type is required" }]}>
              <Select options={[{ value: 'Luxury' }, { value: 'Business' }, { value: 'Budget' }, { value: 'Resort' }]} />
            </Form.Item>
            {authRole !== "airline" ? (
              <Form.Item label="Airport Selection" name="airline_ids" rules={[{ required: true, message: "Airport Selection is required" }]}>
                <Select mode="multiple" options={airlineOptions} placeholder="Select Airport Airline(s)" />
              </Form.Item>
            ) : (
              <Form.Item name="airline_ids" hidden>
                <Select mode="multiple" options={airlineOptions} />
              </Form.Item>
            )}
            {/* <Form.Item label="Commission Rate (%)" name="commissionRate" rules={[{ required: true, message: "Commission rate is required" }]}><InputNumber min={0} max={100} style={{ width: '100%' }} /></Form.Item> */}
          </div>

          <Form.Item label="Address" name="address" rules={[{ required: true, whitespace: true, message: "Address is required" }]}><Input placeholder="Enter full address" /></Form.Item>

          <Divider orientation="left" plain>Timing & Policies</Divider>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0 16px" }}>
            <Form.Item label="Check-in" name="check_in_time">
              <TimePicker
                format="h:mm A"
                placeholder="12:00 PM"
                style={{ width: '100%' }}
                use12Hours
              />
            </Form.Item>
            <Form.Item label="Check-out" name="check_out_time">
              <TimePicker
                format="h:mm A"
                placeholder="11:00 AM"
                style={{ width: '100%' }}
                use12Hours
              />
            </Form.Item>
            <Form.Item label="Pets Allowed" name="pet_allowed" valuePropName="checked"><Switch /></Form.Item>
          </div>

          <Form.Item label="Child Policy" name="child_policy"><Input.TextArea rows={2} placeholder="Enter child policy" /></Form.Item>
          <Form.Item label="Cancellation Policy" name="cancellation_policy"><Input.TextArea rows={2} placeholder="Enter cancellation policy" /></Form.Item>
          <Form.Item label="About Hotel" name="aboutHotel" rules={[{ required: true, whitespace: true, message: "About hotel is required" }]}><Input.TextArea rows={3} placeholder="Write a short property description" /></Form.Item>

          {/* <Form.Item label="Facilities">
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Input
                  placeholder="Enter facility name"
                  value={facilityInput}
                  onChange={(e) => setFacilityInput(e.target.value)}
                  onPressEnter={handleAddFacility}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={handleAddFacility}
                  style={{ flexShrink: 0 }}
                >
                  Add
                </Button>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 8, alignItems: 'flex-start' }}>
              {facilities.map((f, i) => (
                <Tag
                  key={i}
                  style={{
                    marginBottom: 4,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    backgroundColor: '#f0f0f0',
                    border: '1px solid #d9d9d9',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer'
                  }}
                >
                  <span>{f}</span>
                  <span
                    onClick={() => handleRemoveFacility(i)}
                    style={{
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      color: '#999'
                    }}
                  >
                    <CloseOutlined />
                  </span>
                </Tag>
              ))}
            </div>
          </Form.Item> */}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
            <Form.Item
              label="Hotel Email"
              name="hotelEmail"
              rules={[
                { required: true, message: "Hotel email is required" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter hotel email" />
            </Form.Item>
            <Form.Item
              label="Contact No"
              name="fullContact"
              rules={[
                { required: true, whitespace: true, message: "Contact number is required" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value) {
                      return Promise.resolve();
                    }
                    const countryCode = getFieldValue("countryCode") || "";
                    const dialCode = countryCode.replace("+", "");
                    const numberOnly = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;

                    if (/^[0-9]{6,15}$/.test(numberOnly)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Contact number must be 6 to 15 digits"));
                  },
                }),
              ]}
            >
              <PhoneInput
                country={"in"}
                enableSearch
                placeholder="Enter contact number"
                enableLongNumbers={true}
                disableMaxLength={true}
                inputProps={{
                  maxLength: 20,
                }}
                onChange={(value, country) => {
                  const dialCode = country?.dialCode || "";
                  const numberOnly = value.startsWith(dialCode) ? value.slice(dialCode.length) : value;
                  addForm.setFieldsValue({
                    countryCode: `+${dialCode}`,
                    contactNo: numberOnly,
                  });
                }}
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "10px",
                  border: "1px solid #d9d9d9",
                }}
                containerStyle={{
                  width: "100%",
                }}
                buttonStyle={{
                  borderRadius: "10px 0 0 10px",
                  border: "1px solid #d9d9d9",
                  background: "#fff",
                }}
              />
            </Form.Item>
            <Form.Item name="countryCode" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="contactNo" hidden>
              <Input />
            </Form.Item>


          </div>
          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: "Please enter your password",
              },
              {
                min: 8,
                message: "Password must be at least 8 characters long",
              },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
                message:
                  "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
              }
            ]}
          >
            <Input.Password placeholder="Enter password" />
          </Form.Item>

          <Form.Item
            label="Hotel Images"
            rules={[
              {
                validator: (_, value) => {
                  const files = hotelImages || [];

                  const isValid = files.every((file) =>
                    file.type?.startsWith("image/")
                  );

                  if (!isValid) {
                    return Promise.reject(
                      new Error("Only image files are allowed")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Upload
              listType="picture-card"
              fileList={hotelImages}
              onChange={({ fileList }) => setHotelImages(fileList)}
              beforeUpload={(file) => {
                const isImage = file.type.startsWith("image/");

                if (!isImage) {
                  message.error("You can only upload image files!");
                  return Upload.LIST_IGNORE;
                }

                return false;
              }}
              multiple
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            </Upload>
          </Form.Item>

          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setAddModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={addLoading}>Add Hotel</Button>
            </Space>
          </div>
        </Form>
      </Modal>

      <style>{`
        .add-hotel-modal .ant-modal-content {
          border-radius: 14px;
          overflow: hidden;
        }
        .add-hotel-modal .ant-modal-header {
          border-bottom: 1px solid rgba(19, 34, 114, 0.12);
          padding: 18px 22px;
        }
        .add-hotel-modal .ant-modal-body {
          padding: 20px 22px 14px;
        }
        .add-hotel-modal .ant-form-item-label > label {
          font-weight: 600;
          color: rgba(0, 0, 0, 0.82);
        }
        .add-hotel-modal .ant-modal-body .ant-input:not(textarea),
        .add-hotel-modal .ant-modal-body .ant-input-affix-wrapper {
          border-radius: 10px !important;
          min-height: 40px;
        }
        .add-hotel-modal .ant-modal-body textarea.ant-input {
          border-radius: 10px !important;
        }
        .add-hotel-modal .ant-modal-body .ant-select-single .ant-select-selector,
        .add-hotel-modal .ant-modal-body .ant-select:not(.ant-select-customize-input) .ant-select-selector {
          border-radius: 10px !important;
          min-height: 40px !important;
          height: 40px !important;
          display: flex !important;
          align-items: center !important;
        }
        .add-hotel-modal .ant-modal-body .ant-select.ant-select-multiple:not(.ant-select-customize-input) .ant-select-selector {
            height: 100% !important;
            min-height: 40px !important;
          }
            .add-hotel-modal .ant-modal-body .ant-select.ant-select-multiple {
            height: 100% !important;
            min-height: 40px !important;
          }
              .add-hotel-modal .ant-modal-body .ant-select.ant-select-multiple input{
            height: 40px !important;
            min-height: 40px !important;
          }
              .ant-select-multiple .ant-select-selection-placeholder { 
              right: 20px;
              left: 20px; 
          }
        .add-hotel-modal .ant-modal-body .ant-input-number {
          width: 100% !important;
          border-radius: 10px !important;
        }
        .add-hotel-modal .ant-divider {
          margin: 16px 0 !important;
        }
      `}</style>
    </Card>
  );
};

export default HotelList;